import type { ImageInputSchema, ImagePricing } from '../types/image-model.js';
import type { ImageGenCostSource } from '../types/image-gen.js';

export interface ResolvedImageCost {
  costUsd: number;
  costSource: ImageGenCostSource;
  costNote: string | null;
  priceMatchKey: string;
}

/**
 * Walk `pricing.entries` in order. The first entry whose `match` object is a
 * subset of `effectiveInputs` (string-equality per key) wins.
 * Empty `match` object acts as a catch-all fallback entry.
 */
export function resolveImageCost(
  pricing: ImagePricing,
  effectiveInputs: Record<string, unknown>,
): ResolvedImageCost {
  for (const entry of pricing.entries) {
    if (matchesAll(entry.match, effectiveInputs)) {
      return {
        costUsd: entry.unitPriceUsd,
        costSource: 'rule',
        costNote: null,
        priceMatchKey: entry.key,
      };
    }
  }

  if (pricing.fallbackUsd != null && isFinite(pricing.fallbackUsd)) {
    return {
      costUsd: pricing.fallbackUsd,
      costSource: 'estimate',
      costNote: 'No pricing entry matched; used pricing.fallbackUsd',
      priceMatchKey: 'fallback',
    };
  }

  return {
    costUsd: 0,
    costSource: 'estimate',
    costNote: 'No pricing entry matched and no fallback configured; cost unknown',
    priceMatchKey: 'no-match',
  };
}

function matchesAll(
  match: Record<string, string | number>,
  inputs: Record<string, unknown>,
): boolean {
  for (const [k, v] of Object.entries(match)) {
    if (String(inputs[k]) !== String(v)) return false;
  }
  return true;
}

/**
 * Merge schema `defaultValue` and fixed params into a user-provided input map.
 * Pricing entries often match on enum values that the user hasn't explicitly
 * set — without this, the match falls through to `fallbackUsd` which produces
 * the wrong (usually highest) price.
 *
 * Precedence: fixed > user input > field.defaultValue
 */
export function applyInputDefaults(
  schema: ImageInputSchema,
  inputs: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {};
  for (const field of schema.fields) {
    if (field.defaultValue !== undefined) {
      merged[field.key] = field.defaultValue;
    }
  }
  Object.assign(merged, inputs);
  Object.assign(merged, schema.fixed ?? {});
  return merged;
}

/**
 * Convenience: compute cost using schema defaults for unset fields.
 */
export function resolveImageCostWithDefaults(
  schema: ImageInputSchema,
  pricing: ImagePricing,
  inputs: Record<string, unknown>,
): ResolvedImageCost {
  return resolveImageCost(pricing, applyInputDefaults(schema, inputs));
}
